import { useQuery } from '@tanstack/react-query';
import { ordersApi, stationViewApi, stationsApi } from '../api/client';

export function useOrders(clientId: string | null, status?: string) {
  return useQuery({
    queryKey: ['kitchen-orders', clientId, status],
    queryFn: () => ordersApi.list(clientId!, { status }),
    enabled: !!clientId,
    // Polling 5s — allineato al refresh dell'app cliente al tavolo, cosi'
    // un ordine self-service del cliente arriva in vista chef entro pochi
    // secondi anche senza WebSocket broadcast.
    refetchInterval: 5_000,
  });
}

export function useStationOrders(stationId: string | null, clientId: string | null) {
  return useQuery({
    queryKey: ['kitchen-station-orders', stationId, clientId],
    queryFn: () => stationViewApi.get(stationId!, clientId!),
    enabled: !!stationId && !!clientId,
    refetchInterval: 5_000,
  });
}

// Feature 148 (US2) — Storico ondate del cuoco.
// Query ISOLATA dalla vista live: queryKey distinta e NESSUN refetchInterval
// (snapshot on-demand, FR-010a). `enabled` solo quando il toggle "Storico" è
// attivo. `refetch` alimenta il pulsante "Aggiorna".
//
// `getOrders` senza `date` filtra su numberDate = giorno UTC corrente, che rolla
// a mezzanotte UTC (≈ 02:00 in Italia), NON al cutoff di giornata lavorativa
// (es. 04:00). Dopo le 2 di notte le comande della serata (bucket di IERI UTC)
// sparivano dallo Storico "come se fosse già il giorno dopo". Per coprire la
// giornata di servizio a cavallo della mezzanotte UTC uniamo il bucket di OGGI
// e quello di IERI (UTC).
export function useHistoryOrders(clientId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['kitchen-history-orders', clientId],
    queryFn: async () => {
      const yesterday = new Date(Date.now() - 86_400_000)
        .toISOString()
        .slice(0, 10);
      const [todayOrders, yesterdayOrders] = await Promise.all([
        ordersApi.list(clientId!),
        ordersApi.list(clientId!, { date: yesterday }),
      ]);
      return [...todayOrders, ...yesterdayOrders];
    },
    enabled: !!clientId && enabled,
    // Snapshot: nessun polling. L'aggiornamento avviene solo via refetch manuale.
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
}

export function useStations(clientId: string | null) {
  return useQuery({
    queryKey: ['kitchen-stations', clientId],
    queryFn: () => stationsApi.list(clientId!),
    enabled: !!clientId,
    staleTime: 60_000,
  });
}
